/**
 * Runtime Configuration Module
 * 
 * Loads configuration from /config.json at runtime (generated by docker-entrypoint.sh).
 * Falls back to build-time REACT_APP_* environment variables for local `npm start`.
 * 
 * Usage:
 *   - Call loadConfig() once before ReactDOM.render() in index.js
 *   - Use getConfig() anywhere in the app to read config values
 */

export interface AppConfig {
  ENABLE_CAPTCHA: boolean;
  RECAPTCHA_SITE_KEY: string;
  API_BASE_URL: string;
  DMS_BASE_URL: string;
  MAINTENANCE_MODE: boolean;
  ENABLE_EMAIL_NOTIFICATION: boolean;
  OFFER_PAYMENT_GATEWAY: string;
}

// Build-time defaults (used as fallback when config.json is unavailable, e.g. local dev)
const defaults: AppConfig = {
  ENABLE_CAPTCHA: process.env.REACT_APP_ENABLE_CAPTCHA === "true",
  RECAPTCHA_SITE_KEY: process.env.REACT_APP_RECAPTCHA_SITE_KEY || "",
  API_BASE_URL: process.env.REACT_APP_GENERATED_BACKEND_URL || "http://localhost:8082/api",
  DMS_BASE_URL: process.env.REACT_APP_DMS_BASE_URL || "http://localhost:8085/api",
  MAINTENANCE_MODE: process.env.REACT_APP_MAINTENANCE_MODE === "true",
  ENABLE_EMAIL_NOTIFICATION: process.env.REACT_APP_ENABLE_EMAIL_NOTIFICATION === "true",
  OFFER_PAYMENT_GATEWAY: process.env.REACT_OFFER_PAYMENT_GATEWAY || "ICICI"
};

// Mutable config populated by loadConfig()
let config: AppConfig = { ...defaults };

/**
 * Fetches /config.json (generated at container startup by docker-entrypoint.sh)
 * and merges it into the runtime config. Falls back to build-time env vars
 * if the fetch fails (e.g. during local development with `npm start`).
 */
export const loadConfig = async (): Promise<AppConfig> => {
  try {
    const response = await fetch("/config.json", { cache: "no-store" });
    if (response.ok) {
      const json = await response.json();
      config = {
        ENABLE_CAPTCHA: json.ENABLE_CAPTCHA ?? defaults.ENABLE_CAPTCHA,
        RECAPTCHA_SITE_KEY: json.RECAPTCHA_SITE_KEY ?? defaults.RECAPTCHA_SITE_KEY,
        API_BASE_URL: json.API_BASE_URL ?? defaults.API_BASE_URL,
        DMS_BASE_URL: json.DMS_BASE_URL ?? defaults.DMS_BASE_URL,
        MAINTENANCE_MODE: json.MAINTENANCE_MODE ?? defaults.MAINTENANCE_MODE,
        ENABLE_EMAIL_NOTIFICATION: json.ENABLE_EMAIL_NOTIFICATION ?? defaults.ENABLE_EMAIL_NOTIFICATION,
        OFFER_PAYMENT_GATEWAY: json.OFFER_PAYMENT_GATEWAY ?? defaults.OFFER_PAYMENT_GATEWAY

      };
      console.log("[RuntimeConfig] Loaded from /config.json:", {
        ENABLE_CAPTCHA: config.ENABLE_CAPTCHA,
        API_BASE_URL: config.API_BASE_URL,
        MAINTENANCE_MODE: config.MAINTENANCE_MODE,
        OFFER_PAYMENT_GATEWAY: config.OFFER_PAYMENT_GATEWAY,
        HAS_SITE_KEY: !!config.RECAPTCHA_SITE_KEY,
      });
    } else {
      console.warn("[RuntimeConfig] /config.json not found (HTTP " + response.status + "), using build-time defaults");
    }
  } catch (err) {
    console.warn("[RuntimeConfig] Failed to fetch /config.json, using build-time defaults:", err);
  }
  return config;
};

/**
 * Get the config synchronously.
 */
export const getConfig = (): AppConfig => config;

/**
 * Helper: returns true when captcha should be shown in the UI.
 * Requires both the feature flag AND a valid site key.
 */
export const isCaptchaEnabled = (): boolean => {
  return config.ENABLE_CAPTCHA === true && !!config.RECAPTCHA_SITE_KEY?.trim();
};
